import React, { useState } from 'react';
import { observer } from 'mobx-react';
import { TabPaneProps, Tabs, Tooltip } from 'antd';
import { SaveOutlined } from '@ant-design/icons';
import { Catcher } from '../../';
import { EditRateCompaniesAssigned, EditRateFormInfoV2 } from './';
import { EditRatesV2 } from './EditRatesV2';
import cx from 'classnames';
import { IEditForm, RareFormTab } from '../../../interfaces';
import { usePagesStoreCurrentComponent, useRateChangedFormTabs, useRateInfo } from '../../../hooks';
import { showConfirm } from '../../../instruments';
import { tI, tP } from '../../../translate';
import Styles from '../../styles/m_itemCardStyles.less';

export interface IRateTabs extends Omit<TabPaneProps, 'tab'> {
    key: RareFormTab;
    label: React.ReactNode;
}

export const EditRateFormsV2: React.FC<IEditForm> = observer(
    ({ setEditMode, isNew = false, loadingTypes }: IEditForm) => {
        const changedFormTabs = useRateChangedFormTabs();
        const [currentTab, setCurrentTab] = useState<string>('info');

        const changeCurrentTab = (newTab: string) => {
            if (changedFormTabs.includes(currentTab as RareFormTab)) {
                showConfirm({
                    content: 'Change form without saving?',
                    onOk: () => {
                        setCurrentTab(newTab);
                    },
                });
            } else {
                setCurrentTab(newTab);
            }
        };

        const tabItems: Array<IRateTabs> = [
            {
                label: (
                    <span>
                        {tP('Info')}
                        <Tooltip
                            title={changedFormTabs.includes(RareFormTab.INFO) ? tI('Need to save data') : tI('Saved')}
                        >
                            <SaveOutlined
                                className={cx(
                                    Styles.icon,
                                    changedFormTabs.includes(RareFormTab.INFO) ? Styles.iconFail : Styles.iconOk,
                                )}
                            />
                        </Tooltip>
                    </span>
                ),
                key: RareFormTab.INFO,
                children: (
                    <EditRateFormInfoV2
                        setEditMode={setEditMode}
                        isNew={isNew}
                        loadingTypes={loadingTypes}
                        isDisabledSave={!changedFormTabs.includes(RareFormTab.INFO)}
                    />
                ),
            },
            {
                label: (
                    <span>
                        {tP('Companies')}
                        <Tooltip
                            title={
                                changedFormTabs.includes(RareFormTab.COMPANIES) ? tI('Need to save data') : tI('Saved')
                            }
                        >
                            <SaveOutlined
                                className={cx(
                                    Styles.icon,
                                    changedFormTabs.includes(RareFormTab.COMPANIES) ? Styles.iconFail : Styles.iconOk,
                                )}
                            />
                        </Tooltip>
                    </span>
                ),
                key: RareFormTab.COMPANIES,
                children: <EditRateCompaniesAssigned loadingTypes={loadingTypes} />,
            },
            {
                label: (
                    <span>
                        {tP('Rates')}
                        <Tooltip
                            title={changedFormTabs.includes(RareFormTab.RATES) ? tI('Need to save data') : tI('Saved')}
                        >
                            <SaveOutlined
                                className={cx(
                                    Styles.icon,
                                    changedFormTabs.includes(RareFormTab.RATES) ? Styles.iconFail : Styles.iconOk,
                                )}
                            />
                        </Tooltip>
                    </span>
                ),
                key: RareFormTab.RATES,
                children: <EditRatesV2 loadingTypes={loadingTypes} />,
            },
        ];

        const { name } = useRateInfo();
        const { title: routeTitle } = usePagesStoreCurrentComponent();
        return (
            <Catcher>
                <div className={Styles.main}>
                    <div className={Styles.mainTitleX}>
                        {routeTitle}
                        <span>{name ? ': ' + name : ''}</span>
                    </div>
                    <div className={Styles.tabs}>
                        <Tabs
                            activeKey={currentTab}
                            items={tabItems.filter((i) => !isNew || i.key === RareFormTab.INFO)}
                            onChange={changeCurrentTab}
                        />
                    </div>
                </div>
            </Catcher>
        );
    },
);
